/**
 * PATH: apps/hr/src/lib/api/authHelpers.ts
 * PURPOSE: Auth token / org / user resolution for HR Hub API params
 *
 * Convergence C4-MR1: these helpers used to hand-parse raw browser
 * cookies (duplicating @quotech/auth). They now delegate to the canonical
 * @quotech/auth cookie helpers + Zustand store — the same sources the
 * shared @quotech/api client uses — with the old localStorage keys kept
 * as last-resort fallbacks (legacyBridge still writes them on some hosts).
 *
 * NOTE: the Authorization header itself is attached by the shared client
 * (setupHubApi). getAccessToken stays exported only for callers that need
 * the raw token outside the axios client.
 */

import { getAuthCookie, getOrgCookie, useAuthStore } from '@quotech/auth'

export function getAccessToken(): string | null {
  const cookieToken = getAuthCookie()
  if (cookieToken) return cookieToken
  const storeToken = useAuthStore.getState().accessToken
  if (storeToken) return storeToken
  return localStorage.getItem('AccessToken')
    || localStorage.getItem('accessToken')
    || localStorage.getItem('IdToken')
    || localStorage.getItem('access_token')
    || null
}

export function getOrgId(): string | null {
  const cookieOrg = getOrgCookie()
  if (cookieOrg?.id) return String(cookieOrg.id)
  const { organisationId, user } = useAuthStore.getState()
  if (organisationId) return String(organisationId)
  if (user?.organisation_id) return String(user.organisation_id)
  return localStorage.getItem('CurrentTenantId')
    || localStorage.getItem('org_id')
    || localStorage.getItem('organisation_id')
    || null
}

export function getUserId(): string | null {
  const storeUser = useAuthStore.getState().user
  if (storeUser?.id) return String(storeUser.id)
  try {
    const profileData = localStorage.getItem('ProfileData')
    if (profileData) {
      const profile = JSON.parse(profileData)
      return profile.id?.toString() || profile.user_id?.toString() || null
    }
  } catch { /* Ignore parse errors */ }
  return localStorage.getItem('user_id') || null
}

const DEV_DEFAULT_COMPANY_ID = '1'

export function withCompanyId(params: Record<string, string> = {}): Record<string, string> {
  const companyId = getOrgId()
  const userId = getUserId()
  params.company_id = companyId || DEV_DEFAULT_COMPANY_ID
  const isUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  if (userId && isUuid(userId)) params.user_id = userId
  return params
}

export const DEV_DEFAULT_USER_ID = '00000000-0000-0000-0000-000000000001'
